import type { FC, ReactNode } from "react";
import { useRef } from "react";
import { createPortal } from "react-dom";

import { RoundButton } from "@ui/components/RoundButton";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Container } from "@jti/core-components/components/Container";

import styles from "./PopupVideoDialog.module.scss";

type PopupVideoDialogProps = {
  handleClose: () => void;
  videoPlayer: ReactNode;
  isPopupOpen: boolean;
};

export const PopupVideoDialog: FC<PopupVideoDialogProps> = ({ videoPlayer, handleClose, isPopupOpen }) => {
  const { __ } = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(containerRef, handleClose, {
    excludeSelectors: [`.${styles.closeIcon}`],
  });

  if (!isPopupOpen) {
    return null;
  }

  return createPortal(
    <div className={classNames("dark-theme", styles.dialogContainer)} data-testid="popupVideoDialog">
      <div className={styles.closeIcon}>
        <RoundButton icon={UiCrossIcon} onClick={handleClose} aria-label={__("common.close")} />
      </div>
      <Container className={styles.contentContainer}>
        <div ref={containerRef} className={styles.videoContainer}>
          {videoPlayer}
        </div>
      </Container>
    </div>,
    document.body
  );
};
